/* =============================================================
   reward-discount.js — BMW-styled Reward & Discount interactions
   • Scroll progress bar
   • IntersectionObserver reveal (.rd-reveal)
   • Pretext-inspired Canvas accordion height measurement
   • Hero stagger entrance
   • Counter animation for tier numbers
   ============================================================= */
(function () {
  'use strict';

  /* ── Canvas text height measurement (Pretext pattern) ─────────
     Measures exact pixel height for accordion panels to avoid
     max-height:9999px layout jump. Uses the same font string as CSS.
  ----------------------------------------------------------------- */
  var _ptxCache = new Map();

  function ptxMeasure(text, fontStr, containerWidth, lineHeightPx) {
    var key = fontStr + '|' + Math.round(containerWidth) + '|' + text.slice(0, 80);
    if (_ptxCache.has(key)) return _ptxCache.get(key);

    var canvas = document.createElement('canvas');
    var ctx    = canvas.getContext('2d');
    ctx.font   = fontStr;

    var words   = text.split(/\s+/);
    var lines   = 0;
    var current = '';

    for (var i = 0; i < words.length; i++) {
      var candidate = current ? current + ' ' + words[i] : words[i];
      if (ctx.measureText(candidate).width > containerWidth && current) {
        lines++;
        current = words[i];
      } else {
        current = candidate;
      }
    }
    if (current) lines++;

    var result = { lineCount: Math.max(1, lines), height: Math.max(1, lines) * lineHeightPx };
    _ptxCache.set(key, result);
    return result;
  }

  function measureHeight(text, fontStr, containerWidth, lineHeightPx) {
    if (window.__bmwPretext && typeof window.__bmwPretext.measureBlockHeightCached === 'function') {
      return window.__bmwPretext.measureBlockHeightCached(text, fontStr, containerWidth, lineHeightPx);
    }
    return ptxMeasure(text, fontStr, containerWidth, lineHeightPx).height;
  }

  /* ── Accordion ─────────────────────────────────────────────── */
  function initAccordions() {
    var btns = document.querySelectorAll('.rd__accordion-btn');
    if (!btns.length) return;

    btns.forEach(function (btn) {
      var body  = btn.nextElementSibling;
      var inner = body && body.querySelector('.rd__accordion-inner');
      if (!body || !inner) return;

      btn.addEventListener('click', function () {
        var expanded = btn.getAttribute('aria-expanded') === 'true';

        if (expanded) {
          /* Collapse */
          body.style.height = body.scrollHeight + 'px';
          requestAnimationFrame(function () {
            body.style.height = '0';
          });
          btn.setAttribute('aria-expanded', 'false');
        } else {
          /* Expand — measure with Pretext-style canvas */
          var fullText   = inner.textContent.replace(/\s+/g, ' ').trim();
          var fontStr    = '300 14px Inter, system-ui, sans-serif';
          var lineHeight = 24; /* 14px × 1.7 ≈ 24px, matches CSS */
          var padY       = 20; /* rd__accordion-inner padding-top */
          var containerW = inner.getBoundingClientRect().width || inner.offsetWidth || 320;

          var measuredH = measureHeight(fullText, fontStr, containerW, lineHeight);
          /* Add padding + any embedded strong/a elements (approx +20%) */
          var targetH   = Math.ceil(measuredH * 1.2) + padY;

          body.style.height = targetH + 'px';
          btn.setAttribute('aria-expanded', 'true');

          /* After transition settle, switch to auto for reflows */
          body.addEventListener('transitionend', function onEnd() {
            if (btn.getAttribute('aria-expanded') === 'true') {
              body.style.height = 'auto';
            }
            body.removeEventListener('transitionend', onEnd);
          });
        }
      });
    });
  }

  /* ── Progress bar ── */
  function initProgressBar() {
    var bar = document.querySelector('.rd__progress');
    if (!bar) return;
    function update() {
      var doc = document.documentElement;
      var top = doc.scrollTop || document.body.scrollTop;
      var max = (doc.scrollHeight || document.body.scrollHeight) - doc.clientHeight;
      bar.style.width = (max > 0 ? Math.min(100, (top / max) * 100) : 0).toFixed(2) + '%';
    }
    window.addEventListener('scroll', update, { passive: true });
    update();
  }

  /* ── Scroll reveal ── */
  function initReveal() {
    var targets = document.querySelectorAll('.rd-reveal');
    if (!targets.length) return;

    if (!('IntersectionObserver' in window)) {
      targets.forEach(function (el) { el.classList.add('visible'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) {
          e.target.classList.add('visible');
          io.unobserve(e.target);
        }
      });
    }, { threshold: 0.1 });

    targets.forEach(function (el) { io.observe(el); });
  }

  /* ── Hero stagger ── */
  function initHeroStagger() {
    var els = document.querySelectorAll('.rd__hero [data-stagger]');
    if (!els.length) return;
    els.forEach(function (el, i) {
      el.style.opacity        = '0';
      el.style.transform      = 'translateY(14px)';
      el.style.transition     = 'opacity 0.5s cubic-bezier(0.16,1,0.3,1), transform 0.5s cubic-bezier(0.16,1,0.3,1)';
      el.style.transitionDelay = (i * 0.1 + 0.1) + 's';
      setTimeout(function () {
        el.style.opacity   = '1';
        el.style.transform = 'none';
      }, 60);
    });
  }

  /* ── Counter animation on tier numbers ── */
  function animateCounter(el, target, suffix, duration) {
    if (suffix === void 0) suffix = '';
    if (duration === void 0) duration = 900;
    var start     = performance.now();
    var startVal  = 0;
    function step(now) {
      var progress = Math.min((now - start) / duration, 1);
      /* easeOutExpo */
      var eased = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
      var val   = Math.round(startVal + (target - startVal) * eased);
      el.textContent = val + suffix;
      if (progress < 1) requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
  }

  function initCounters() {
    var counters = document.querySelectorAll('[data-counter]');
    if (!counters.length || !('IntersectionObserver' in window)) return;

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        var el     = e.target;
        var target = parseInt(el.getAttribute('data-counter'), 10);
        var suffix = el.getAttribute('data-counter-suffix') || '';
        animateCounter(el, target, suffix);
        io.unobserve(el);
      });
    }, { threshold: 0.3 });

    counters.forEach(function (el) { io.observe(el); });
  }

  /* ── Tier card hover accent ── */
  function initTierHover() {
    var cards = document.querySelectorAll('.rd__tier-card:not(.rd__tier-card--highlight)');
    cards.forEach(function (card) {
      card.addEventListener('mouseenter', function () {
        card.style.borderTop = '2px solid var(--rd-primary)';
        card.style.paddingTop = (parseInt(getComputedStyle(card).paddingTop) - 2) + 'px';
      });
      card.addEventListener('mouseleave', function () {
        card.style.borderTop = '';
        card.style.paddingTop = '';
      });
    });
  }

  /* ── Main init ── */
  function init() {
    initProgressBar();
    initReveal();
    initHeroStagger();
    initAccordions();
    initCounters();
    initTierHover();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);

})();
